/* Prüf-Service: ein Aufruf liefert Befunde und Auslastung für Karte (Doc, .map-Text) oder kompilierte BSP.
 * Wird vom Prüf-Dialog und von der Kommandozeile gleichermaßen genutzt. Ohne DOM, Dateien und Prozesse; Ein- und Ausgabe sind JSON-tauglich. */
import type { CheckItem, Doc } from '../model/types.ts';
import { applyFix } from '../model/checkfix.ts';
import { isAutoFix } from '../model/fixes.ts';
import Core from '../core.ts';
import Limits from '../analysis/limits.ts';
import type { BspLimitRow, Estimate, Level } from '../analysis/limits.ts';
import Bsp from '../formats/bsp.ts';
import Decomp from '../formats/decomp.ts';
import I18n from '../i18n.ts';

export type Bytes = ArrayBuffer | Uint8Array;
export interface CheckOptions { lang?: string; skipLeak?: boolean; hasTex?: (name: string) => boolean; texSize?: (name: string) => { w: number; h: number } | null }
/** Verweis auf Entities (stabil über Aufrufe hinweg, anders als Brush-IDs ohne gesicherte ID im Text) */
export interface ItemRef { ent: number; classname: string; brushes: number }
export type ReportItem = CheckItem & { refs?: ItemRef[] };
export interface ClipLimits { est: Estimate; level: Level; edicts: number; edictLevel: Level }
export interface Report {
  source: 'map' | 'bsp';
  items: ReportItem[];
  worst: { level: 'err' | 'warn' | null; err: number; warn: number };
  clip: ClipLimits | { error: string } | null;
  bsp: BspLimitRow[] | null;
  warnings: string[];
}

export const toBuffer = (b: Bytes): ArrayBuffer => (ArrayBuffer.isView(b) ? (b as Uint8Array).buffer.slice(b.byteOffset, b.byteOffset + b.byteLength) as ArrayBuffer : b as ArrayBuffer);   // isView statt instanceof: gilt auch für Puffer aus einem anderen Kontext (iframe, jsdom)

/** Führt fn in der gewünschten Sprache aus, ohne die Einstellung des Aufrufers (Speicher, Listener) zu verändern. */
function inLang<T>(lang: string | undefined, fn: () => T): T {
  if (!lang || lang === I18n.lang || !(lang in I18n.LANGS)) return fn();
  const prev = I18n.lang;
  I18n.lib.changeLanguage(lang);
  try { return fn(); } finally { I18n.lib.changeLanguage(prev); }
}

function refsOf(doc: Doc, sel: NonNullable<CheckItem['sel']>): ItemRef[] {
  const byEnt = new Map<string, number>(), byBrush = new Map<string, number>();
  doc.ents.forEach((e, i) => { byEnt.set(e.id, i); e.brushes.forEach((b) => byBrush.set(b.id, i)); });
  const per = new Map<number, number>();
  sel.e.forEach((id) => { const i = byEnt.get(id); if (i !== undefined && !per.has(i)) per.set(i, 0); });
  sel.b.forEach((id) => { const i = byBrush.get(id); if (i !== undefined) per.set(i, (per.get(i) || 0) + 1); });
  return [...per].map(([ent, brushes]) => ({ ent, classname: doc.ents[ent].kv.classname || '', brushes }));
}

/** Befunde einer Karte; dieselben Einträge wie im Prüf-Dialog, zusätzlich mit Verweisen. */
export function checkDoc(doc: Doc, opts?: CheckOptions): Report {
  const o = opts || {};
  const items: ReportItem[] = inLang(o.lang, () => Core.validate(doc, { hasTex: o.hasTex, texSize: o.texSize, skipLeak: o.skipLeak }));
  items.forEach((it) => { if (it.sel) it.refs = refsOf(doc, it.sel); });
  return { source: 'map', items, worst: Core.worstLevel(items), clip: clipLimits(doc), bsp: null, warnings: [] };
}

/** Clipnode-Schätzung und Entity-Zahl; scheitert die Schätzung, steht der Grund in error */
export function clipLimits(doc: Doc): ClipLimits | { error: string } {
  try {
    const e = Limits.estimate(Limits.classify(doc));
    return { est: e, level: Limits.level(e), edicts: doc.ents.length, edictLevel: Limits.edictLevel(doc.ents.length) };
  } catch (err) { return { error: (err as Error).message }; }
}

export function checkText(text: string, opts?: CheckOptions): Report {
  const r = Core.parseText(text);
  if (!r.doc) return { source: 'map', items: [{ level: 'err', id: 'parse', text: r.warnings.join(' ') || 'parse failed' }], worst: { level: 'err', err: 1, warn: 0 }, clip: null, bsp: null, warnings: r.warnings };
  const rep = checkDoc(r.doc, opts);
  rep.warnings = r.warnings;
  return rep;
}

/** Kompilierte BSP: Auslastung der Engine-Grenzen, dazu die Befunde des dekompilierten Textes */
export function checkBsp(bytes: Bytes, opts?: CheckOptions): Report {
  const buf = toBuffer(bytes);
  const rows = Limits.bspRows(Bsp.parse(buf), 9);
  const d = Decomp.decompile(buf);
  const rep = checkText(Decomp.toMapText(d), opts);
  rep.source = 'bsp'; rep.bsp = rows; rep.warnings = d.warnings.concat(rep.warnings);
  return rep;
}

/** Wendet die automatischen Lösungen der Befunde mit den genannten IDs an (ohne Angabe: alle lösbaren, die `auto` sind, siehe model/fixes.ts; Lösungen, die das Spielverhalten ändern, nur mit ausdrücklicher ID). skipped: genannte IDs ohne lösbaren Befund. */
export function applyFixes(doc: Doc, ids?: string[], opts?: CheckOptions): { doc: Doc; applied: { id: string; fix: string; count: number }[]; skipped: string[] } {
  const want = ids && new Set(ids), applied: { id: string; fix: string; count: number }[] = [], done = new Set<string>();
  for (let guard = 0; guard < 32; guard++) {
    const it = checkDoc(doc, opts).items.find((x) => x.fix && x.sel && x.id && !done.has(x.id) && (want ? want.has(x.id) : isAutoFix(x.fix)));
    if (!it || !it.fix || !it.sel || !it.id) break;
    done.add(it.id);
    const r = applyFix(doc, it.fix, it.sel);
    if (r.count) { doc = r.doc; applied.push({ id: it.id, fix: it.fix, count: r.count }); }
  }
  const got = new Set(applied.map((a) => a.id));
  return { doc, applied, skipped: ids ? ids.filter((i) => !got.has(i)) : [] };
}
